// Client-side prediction for the local player's tank.
//
// The deterministic sim is the same on both sides, so the client maintains
// a "predicted me" — a Tank struct it advances at the sim rate (50 Hz) using
// the player's current input. The renderer draws this predicted position,
// so input → visible movement has zero round-trip latency.
//
// Reconciliation: every server snapshot tells us where the server thinks
// our tank is. If the divergence is small (≤ 2 tiles), we trust our
// prediction and ignore the snapshot for our tank. If it's large (we got
// killed, mined, refuelled, teleported by respawn), we snap to the
// server's authoritative position.
//
// Sub-tick interpolation: the sim ticks at 50 Hz (20 ms) but we render at
// the display rate (often 60+ Hz). To avoid stair-stepping at high speeds,
// we keep the position the shadow had *before* the latest tick step and
// lerp from there toward the current shadow as wall-clock time progresses
// across the next 20 ms. Adds <20 ms of local input lag, well below the
// perceptual threshold; eliminates the 1-frame/1-frame/2-frame motion
// pattern visible during fast driving.
//
// This module covers ONLY tank position/heading/speed. Damage events,
// resource counts, ammo etc. are still driven by snapshots — when the
// server says we took 5 damage, that's truth.

import { MAP_SIZE, TERRAIN_MASK, Terrain, TICK_MS, WORLD_UNITS_PER_TILE } from '@bolo2k/shared';
import { predictBoatMovement, predictTankMovement, type Tank, type TankInput } from '@bolo2k/sim';
import type { ClientState, ClientTank } from './clientState.js';

const HARD_SNAP_DISTANCE = WORLD_UNITS_PER_TILE * 2; // 2 tiles

// Lightweight Tank shadow with just the fields predictTankMovement reads/writes.
// (Snapshots from the server are partial views, so we can't reuse the full
// sim Tank struct directly — it has fields like `armour`/`shells` that
// snapshots don't carry the way the sim expects.)
function makeShadow(snapshot: ClientTank): Tank {
  return {
    id: snapshot.id,
    owner: snapshot.owner,
    alive: snapshot.alive,
    x: snapshot.x,
    y: snapshot.y,
    dir: snapshot.dir,
    speed: snapshot.speed,
    reload: 0,
    armour: snapshot.armour,
    shells: snapshot.shells,
    mines: snapshot.mines,
    trees: snapshot.trees,
    carriedPills: snapshot.carriedPills,
    turnHoldTicks: 0,
    slideTicks: 0,
    slideDir: 0,
    slideDist: 0,
    obstructed: false,
    waterCount: 0,
    sightLen: snapshot.sightLen,
    sightLenTimer: 0,
    kills: snapshot.kills,
    deaths: snapshot.deaths,
    // pillsBuilt/basesCaptured are server-side stats (not in the wire
    // snapshot). The predictor's tank shadow never reads them — zero stub.
    pillsBuilt: 0,
    basesCaptured: 0,
    lastDamageByTankId: 0,
    startIdx: 0,
    inBoatId: snapshot.inBoatId,
    lgm: {
      status: snapshot.lgmStatus as 0,
      x: snapshot.lgmX,
      y: snapshot.lgmY,
      job: null,
      buildTimer: 0,
      blessX: -1,
      blessY: -1,
    },
    pendingBuild: null,
  };
}

export class Predictor {
  // `shadow` is the authoritative tick-stepped position — what reconcile
  // mutates and what predictTankMovement reads on the next step. Always
  // points at the latest fully-simulated tick boundary.
  private shadow: Tank | null = null;
  // `viewShadow` is what predict() returns to the renderer — same as shadow
  // but with x/y/dir sub-tick-interpolated between the previous and current
  // tick. Reusing a single Tank object avoids per-frame GC churn.
  private viewShadow: Tank | null = null;
  // Position/heading the shadow held *before* the latest tick step. We lerp
  // from this toward shadow as the accumulator progresses 0 → TICK_MS,
  // producing smooth motion at any render rate.
  private prevX = 0;
  private prevY = 0;
  private prevDir = 0;
  private lastTickTime = 0;
  private accumulator = 0;

  // Returns the predicted tank (or null if we haven't received a welcome yet).
  // While afloat, the shadow tracks the BOAT's x/y/dir/speed (server sets
  // tank.x = boat.x in tickBoats, so the visible position matches whichever
  // role the rider is in). predictBoatMovement is called instead of
  // predictTankMovement inside the per-tick advance loop; reconcile snaps
  // the shadow against state.boats[…matching id…] instead of `me`.
  predict(state: ClientState, input: TankInput, now: number = performance.now()): Tank | null {
    const me = state.tanks.find((t) => t.id === state.myTankId);
    if (!me || !me.alive) {
      this.shadow = null;
      this.viewShadow = null;
      this.lastTickTime = now;
      this.accumulator = 0;
      return null;
    }

    if (!this.shadow) {
      this.shadow = makeShadow(me);
      this.viewShadow = makeShadow(me);
      // While afloat the shadow's speed has to track the boat's speed, not
      // the tank's (which the server keeps at 0). Seed from the matching
      // boat entity if available.
      if (me.inBoatId !== 0) {
        const boat = state.boats.find((b) => b.id === me.inBoatId);
        if (boat) {
          this.shadow.x = boat.x;
          this.shadow.y = boat.y;
          this.shadow.dir = boat.dir;
          this.shadow.speed = boat.speed;
          this.viewShadow.x = boat.x;
          this.viewShadow.y = boat.y;
          this.viewShadow.dir = boat.dir;
          this.viewShadow.speed = boat.speed;
        }
      }
      this.prevX = this.shadow.x;
      this.prevY = this.shadow.y;
      this.prevDir = this.shadow.dir;
      this.lastTickTime = now;
      this.accumulator = 0;
      return this.viewShadow;
    }

    // Advance one sim tick per TICK_MS of wall-clock since last call.
    if (this.lastTickTime === 0) this.lastTickTime = now;
    this.accumulator += now - this.lastTickTime;
    this.lastTickTime = now;
    let safety = 0;
    while (this.accumulator >= TICK_MS && safety++ < 8) {
      this.accumulator -= TICK_MS;
      // Capture the pre-step position so the next render frame can lerp
      // smoothly from here toward the new shadow position over the next
      // TICK_MS of wall-clock.
      this.prevX = this.shadow.x;
      this.prevY = this.shadow.y;
      this.prevDir = this.shadow.dir;
      if (this.shadow.inBoatId !== 0) {
        // Boat mode — mirror tickBoats. Shadow's x/y/dir/speed ARE the
        // boat's; predictBoatMovement steers, throttles, and drifts them.
        const r = predictBoatMovement(state.terrain, this.shadow, input);
        if (r.beached) {
          // Predicted landfall. Gate further boat prediction; the next
          // server snapshot will confirm (set me.inBoatId = 0) and the
          // tank-prediction path takes over. Until then, leave the
          // shadow's x/y where the beach happened so the camera stays
          // put rather than snapping back to the in-water position the
          // server still believes in.
          this.shadow.inBoatId = 0;
        }
      } else {
        const m = predictTankMovement(state.terrain, this.shadow, input);
        // Mirror sim/movement.ts pickupBoatTile so the predictor stops
        // pushing the tank forward the instant it crosses onto a placed
        // Boat tile. Without this the predictor keeps land-driving the
        // tank for ~100 ms (the snapshot interval) until the server tells
        // us we're afloat — and the camera visibly snaps when the boat
        // entity catches up. The local terrain isn't mutated (that comes
        // via the snapshot's terrain delta a tick later) — just enough
        // state to gate further prediction.
        if (m.entered) {
          const idx = m.nextTileY * MAP_SIZE + m.nextTileX;
          const cell = state.terrain[idx] ?? 0;
          if ((cell & TERRAIN_MASK) === Terrain.Boat) {
            // Sentinel id — the server's authoritative boatId arrives in
            // the next snapshot; reconcile() overwrites it then. The
            // shadow inherits the tank's pre-step speed (matches
            // pickupBoatTile on the server), so the boat starts moving
            // at the tank's current velocity — no jarring pause.
            this.shadow.inBoatId = -1;
          }
        }
      }
    }

    // Sub-tick interpolation. frac in [0..1] grows as wall-clock advances
    // from the latest tick boundary toward the next. Renders a position
    // 0..TICK_MS behind "now", which is invisible to the player but
    // eliminates the visible stair-step at high tank speeds.
    const frac = Math.max(0, Math.min(1, this.accumulator / TICK_MS));
    const v = this.viewShadow!;
    // Copy non-position fields from shadow each frame so HUD/etc. see the
    // freshest tick-step values for reload, armour, etc.
    v.alive = this.shadow.alive;
    v.armour = this.shadow.armour;
    v.shells = this.shadow.shells;
    v.mines = this.shadow.mines;
    v.trees = this.shadow.trees;
    v.carriedPills = this.shadow.carriedPills;
    v.sightLen = this.shadow.sightLen;
    v.inBoatId = this.shadow.inBoatId;
    v.speed = this.shadow.speed;
    v.lgm.status = this.shadow.lgm.status;
    v.lgm.x = this.shadow.lgm.x;
    v.lgm.y = this.shadow.lgm.y;
    v.x = this.prevX + (this.shadow.x - this.prevX) * frac;
    v.y = this.prevY + (this.shadow.y - this.prevY) * frac;
    v.dir = easeDirShort(this.prevDir, this.shadow.dir, frac);

    return v;
  }

  // Called when a fresh snapshot arrives. Reconcile the shadow with the
  // server's view of our tank.
  reconcile(state: ClientState): void {
    const me = state.tanks.find((t) => t.id === state.myTankId);
    if (!me) {
      this.shadow = null;
      this.viewShadow = null;
      return;
    }
    if (!this.shadow) {
      this.shadow = makeShadow(me);
      this.viewShadow = makeShadow(me);
      this.prevX = this.shadow.x;
      this.prevY = this.shadow.y;
      this.prevDir = this.shadow.dir;
      return;
    }
    // Sync resource fields from server (always authoritative).
    this.shadow.armour = me.armour;
    this.shadow.shells = me.shells;
    this.shadow.mines = me.mines;
    this.shadow.trees = me.trees;
    this.shadow.carriedPills = me.carriedPills;
    this.shadow.sightLen = me.sightLen;
    this.shadow.alive = me.alive;
    this.shadow.inBoatId = me.inBoatId;
    this.shadow.lgm.status = me.lgmStatus as 0;
    this.shadow.lgm.x = me.lgmX;
    this.shadow.lgm.y = me.lgmY;
    // While afloat the shadow tracks the BOAT (not the tank — the server
    // keeps tank.x = boat.x but tank.speed = 0). Find the matching boat
    // and reconcile against it; same soft-drift-or-hard-snap shape as the
    // tank path below.
    if (me.inBoatId !== 0) {
      const boat = state.boats.find((b) => b.id === me.inBoatId);
      if (!boat) {
        // We've signed the snapshot saying we're afloat but the boat
        // entity isn't in this snapshot yet (race during pickup). Keep
        // the shadow where it is — the next snapshot will carry the boat.
        return;
      }
      const bdx = boat.x - this.shadow.x;
      const bdy = boat.y - this.shadow.y;
      const bDistSq = bdx * bdx + bdy * bdy;
      if (bDistSq > HARD_SNAP_DISTANCE * HARD_SNAP_DISTANCE) {
        this.shadow.x = boat.x;
        this.shadow.y = boat.y;
        this.shadow.dir = boat.dir;
        this.shadow.speed = boat.speed;
        this.prevX = boat.x;
        this.prevY = boat.y;
        this.prevDir = boat.dir;
      } else {
        this.shadow.x += bdx * 0.1;
        this.shadow.y += bdy * 0.1;
        this.shadow.dir = easeDirShort(this.shadow.dir, boat.dir, 0.5);
        this.shadow.speed += (boat.speed - this.shadow.speed) * 0.5;
      }
      return;
    }

    // Position reconciliation: if we drifted significantly, snap.
    const dx = me.x - this.shadow.x;
    const dy = me.y - this.shadow.y;
    const distSq = dx * dx + dy * dy;
    if (distSq > HARD_SNAP_DISTANCE * HARD_SNAP_DISTANCE) {
      this.shadow.x = me.x;
      this.shadow.y = me.y;
      this.shadow.dir = me.dir;
      this.shadow.speed = me.speed;
      // Snap prev too so the sub-tick lerp doesn't "warp" the visible tank
      // from the pre-snap position to the new one.
      this.prevX = me.x;
      this.prevY = me.y;
      this.prevDir = me.dir;
    } else {
      // Soft drift: ease 10% of the way toward the server position to
      // correct slow accumulated error without a visible jump. Leaving prev
      // unchanged is fine — the soft drift moves shadow by at most 10% of a
      // small delta, which is below perception once lerped.
      this.shadow.x += dx * 0.1;
      this.shadow.y += dy * 0.1;
      // Also ease dir + speed toward server. Without this, network-latency
      // mismatch on turn-key timing leaves shadow.dir off by 1-3 brads
      // forever, and that small offset can land predictor and server on
      // opposite sides of the 16-step movement-snap boundary — causing
      // visible lateral drift and periodic 2-tile snap-backs.
      this.shadow.dir = easeDirShort(this.shadow.dir, me.dir, 0.5);
      this.shadow.speed += (me.speed - this.shadow.speed) * 0.5;
    }
  }
}

// Ease `from` toward `to` by `t` (0..1) on the 0..256 wrapping circle, taking
// the short way around. t=1 returns `to`; t=0 returns `from`.
function easeDirShort(from: number, to: number, t: number): number {
  let d = to - from;
  if (d > 128) d -= 256;
  if (d < -128) d += 256;
  return ((from + d * t) % 256 + 256) % 256;
}
